import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getChemicals, createChemical, updateChemical, getChemicalChanges, deleteChemicalChange, fetchPrice } from '../services/chemicalService';
import { getWashPackageMix } from '../services/plService';
import ScrollHint from '../components/ScrollHint';
import { useLocationContext } from '../contexts/LocationContext';

const ChemicalForm = () => {
  const { currentLocation } = useLocationContext();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    location: 'SS',
    container_size_gallons: 5,
    cost_per_container: '',
    supplier: 'Kleen-Rite',
    product_url: '',
    // null = applies to all packages (default); array of int dollar amounts = explicit subset
    packages: null,
  });
  const [availablePackages, setAvailablePackages] = useState([]); // [10, 14, 16, 18]
  const [existingNames, setExistingNames] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetchingPrice, setFetchingPrice] = useState(false);
  const [error, setError] = useState('');
  const [priceMessage, setPriceMessage] = useState('');

  useEffect(() => {
    fetchExistingNames();
    fetchAvailablePackages();
    if (isEditing) {
      fetchChemical();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentLocation?.id]);

  const fetchAvailablePackages = async () => {
    try {
      const data = await getWashPackageMix();
      // overall has objects with numeric `charge` field (rounded $)
      const charges = (data?.overall || []).map(p => p.charge).filter(c => Number.isFinite(c));
      setAvailablePackages(charges.sort((a, b) => a - b));
    } catch {
      setAvailablePackages([]);
    }
  };

  const fetchExistingNames = async () => {
    try {
      const data = await getChemicals();
      const names = [...new Set(data.chemicals.map(c => c.name))];
      setExistingNames(names);
    } catch (err) {
      // Non-critical, just won't have autocomplete
    }
  };

  const fetchChemical = async () => {
    try {
      setLoading(true);
      const data = await getChemicalChanges(id);
      const chem = data.chemical;
      setFormData({
        name: chem.name,
        location: chem.location,
        container_size_gallons: chem.container_size_gallons,
        cost_per_container: chem.cost_per_container,
        supplier: chem.supplier,
        product_url: chem.product_url || '',
        packages: Array.isArray(chem.packages) ? chem.packages : null,
      });
      setChanges(data.changes);
    } catch (err) {
      setError('Failed to load chemical');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'name') {
      setShowSuggestions(value.length > 0);
    }
  };

  const selectSuggestion = (name) => {
    setFormData(prev => ({ ...prev, name }));
    setShowSuggestions(false);
  };

  const isPackageChecked = (price) => {
    if (formData.packages === null) return true; // null = all
    return Array.isArray(formData.packages) && formData.packages.includes(price);
  };

  const togglePackage = (price) => {
    setFormData(prev => {
      // Materialize null into an explicit "all checked" list before toggling
      const current = prev.packages === null
        ? [...availablePackages]
        : Array.isArray(prev.packages) ? [...prev.packages] : [];
      const idx = current.indexOf(price);
      if (idx >= 0) current.splice(idx, 1);
      else current.push(price);
      return { ...prev, packages: current.sort((a, b) => a - b) };
    });
  };

  const handleFetchPrice = async () => {
    if (!formData.product_url.trim()) {
      setPriceMessage('Enter a product URL first');
      return;
    }
    try {
      setFetchingPrice(true);
      setPriceMessage('');
      const data = await fetchPrice(formData.product_url);
      setFormData(prev => ({ ...prev, cost_per_container: data.price }));
      setPriceMessage(`Price updated: $${data.price.toFixed(2)}${data.name ? ` (${data.name})` : ''}`);
    } catch (err) {
      setPriceMessage('Could not fetch price from this URL');
    } finally {
      setFetchingPrice(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Chemical name is required');
      return;
    }

    try {
      setSaving(true);
      // Packages: null on save means "applies to every package" — preserve that
      // when every detected package is checked, so newly-added tiers are included
      // automatically. Only ship an explicit array when the user chose a subset.
      let packagesPayload = formData.packages;
      if (formData.location !== 'Auto') {
        packagesPayload = null;
      } else if (
        Array.isArray(formData.packages) &&
        availablePackages.length > 0 &&
        availablePackages.every(p => formData.packages.includes(p))
      ) {
        packagesPayload = null;
      }

      const payload = {
        ...formData,
        container_size_gallons: parseFloat(formData.container_size_gallons) || 5,
        cost_per_container: parseFloat(formData.cost_per_container) || 0,
        packages: packagesPayload,
      };

      if (isEditing) {
        await updateChemical(id, payload);
      } else {
        await createChemical(payload);
      }
      navigate('/inventory');
    } catch (err) {
      setError('Failed to save chemical. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteChange = async (changeId) => {
    if (!window.confirm('Delete this change record?')) return;
    try {
      await deleteChemicalChange(changeId);
      setChanges(prev => prev.filter(c => c.id !== changeId));
    } catch (err) {
      setError('Failed to delete change record');
    }
  };

  const getSupplierFromUrl = (url) => {
    if (!url) return null;
    try {
      const hostname = new URL(url.startsWith('http') ? url : 'https://' + url).hostname.toLowerCase();
      if (hostname.includes('kleen-rite')) return 'Kleen-Rite';
      if (hostname.includes('carwashsuperstore')) return 'Car Wash Superstore';
      if (hostname.includes('dultmeier')) return 'Dultmeier';
      if (hostname.includes('windtrax')) return 'Windtrax';
      // Strip www. and extract domain name
      return hostname.replace(/^www\./, '').split('.')[0];
    } catch {
      return null;
    }
  };

  const urlSupplier = getSupplierFromUrl(formData.product_url);

  const filteredSuggestions = existingNames.filter(n =>
    n.toLowerCase().includes(formData.name.toLowerCase()) && n.toLowerCase() !== formData.name.toLowerCase()
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditing ? 'Edit Chemical' : 'Add Chemical'}
        </h1>
        <button
          onClick={() => navigate('/inventory')}
          className="text-gray-600 hover:text-gray-800 text-sm font-medium"
        >
          Back to Inventory
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4 overflow-hidden max-w-full">
        {/* Name */}
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">Chemical Name</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            onFocus={() => formData.name.length > 0 && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. Presoak, Tri-Foam, Tire Cleaner"
            autoComplete="off"
          />
          {showSuggestions && filteredSuggestions.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-40 overflow-y-auto">
              {filteredSuggestions.map(name => (
                <button
                  key={name}
                  type="button"
                  onMouseDown={() => selectSuggestion(name)}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm"
                >
                  {name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
          <select
            name="location"
            value={formData.location}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="SS">SS Bays</option>
            <option value="Auto">Auto Wash</option>
          </select>
        </div>

        {/* Packages — only relevant for Auto Wash chemicals */}
        {formData.location === 'Auto' && availablePackages.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Used in packages</label>
            <p className="text-xs text-gray-500 mb-2">
              Check the wash tiers that use this chemical. If you leave them all
              checked, the cost will be split across every package automatically.
            </p>
            <div className="flex flex-wrap gap-3">
              {availablePackages.map(price => (
                <label
                  key={price}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={isPackageChecked(price)}
                    onChange={() => togglePackage(price)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">${price}</span>
                </label>
              ))}
            </div>
            {Array.isArray(formData.packages) && formData.packages.length === 0 && (
              <p className="text-xs text-amber-600 mt-2">
                No packages selected — this chemical's cost won't be attributed to any tier.
              </p>
            )}
          </div>
        )}

        {/* Container Size */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Container Size (gallons)</label>
          <input
            type="number"
            name="container_size_gallons"
            value={formData.container_size_gallons}
            onChange={handleChange}
            step="0.5"
            min="0.5"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Product URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Product Link</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              name="product_url"
              value={formData.product_url}
              onChange={handleChange}
              className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="https://www.kleen-ritecorp.com/... or other supplier URL"
            />
            <button
              type="button"
              onClick={handleFetchPrice}
              disabled={fetchingPrice}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {fetchingPrice ? 'Fetching...' : 'Fetch Price'}
            </button>
          </div>
          {priceMessage && (
            <p className={`text-sm mt-1 ${priceMessage.startsWith('Could not') || priceMessage.startsWith('Enter') ? 'text-red-600' : 'text-green-600'}`}>
              {priceMessage}
            </p>
          )}
          {formData.product_url && (
            <a
              href={formData.product_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 text-sm mt-1 inline-block"
            >
              View on {urlSupplier || 'supplier site'}
            </a>
          )}
          <p className="text-xs text-gray-400 mt-1">
            Shared fields (cost, size, supplier, link) sync automatically to matching chemicals in other locations.
          </p>
        </div>

        {/* Cost */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Cost per Container ($)</label>
          <input
            type="number"
            name="cost_per_container"
            value={formData.cost_per_container}
            onChange={handleChange}
            step="0.01"
            min="0"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="0.00"
          />
        </div>

        {/* Supplier */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
          <input
            type="text"
            name="supplier"
            value={formData.supplier}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="flex space-x-3 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : (isEditing ? 'Update Chemical' : 'Add Chemical')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/inventory')}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>

      {/* Change History (only when editing) */}
      {isEditing && changes.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Change History</h2>
          </div>
          <ScrollHint>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {changes.map(change => (
                  <tr key={change.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(change.date + 'T12:00:00').toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {change.notes || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleDeleteChange(change.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollHint>
        </div>
      )}
    </div>
  );
};

export default ChemicalForm;
